import type { RouteObject } from 'react-router';
import { RewardsScreen } from '@/features/achievements/RewardsScreen';
import { LearningMapScreen } from '@/features/curriculum/LearningMapScreen';
import { SubjectsScreen } from '@/features/curriculum/SubjectsScreen';
import { HomeScreen } from '@/features/home/HomeScreen';
import { WhatsNewScreen } from '@/features/news/WhatsNew';
import { ProfilesScreen } from '@/features/learners/ProfilesScreen';
import { WelcomeScreen } from '@/features/learners/WelcomeScreen';
import { ReviewScreen } from '@/features/mastery/ReviewScreen';
import { PrivacyScreen } from '@/features/privacy/PrivacyScreen';
import { SettingsScreen } from '@/features/settings/SettingsScreen';
import { ChildLayout, FocusLayout } from './layouts/ChildLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { RouteFallback } from './RouteFallback';
import { NotFound, RouteError } from './RouteError';
import { StartRedirect } from './StartRedirect';

/**
 * Routes. URLs only ever contain random ids and content ids, never personal data.
 * Less frequent areas (playing, lessons, onboarding, parent area) load on demand
 * to keep the first download small on low-end phones.
 */
export const routes: RouteObject[] = [
  {
    errorElement: <RouteError />,
    hydrateFallbackElement: <RouteFallback />,
    children: [
      { index: true, element: <StartRedirect /> },
      {
        element: <PublicLayout />,
        children: [
          { path: 'welcome', element: <WelcomeScreen /> },
          { path: 'profiles', element: <ProfilesScreen /> },
          {
            path: 'profiles/new',
            lazy: () =>
              import('@/features/learners/OnboardingScreen').then((m) => ({
                Component: m.OnboardingScreen,
              })),
          },
          { path: 'privacy', element: <PrivacyScreen /> },
          // Shares its module with the home banner, so it is not split out.
          { path: 'whats-new', element: <WhatsNewScreen /> },
        ],
      },
      {
        path: 'learn',
        element: <ChildLayout />,
        children: [
          { index: true, element: <HomeScreen /> },
          { path: 'subjects', element: <SubjectsScreen /> },
          { path: 'subjects/:subjectId', element: <LearningMapScreen /> },
          {
            path: 'subjects/:subjectId/rules',
            lazy: () =>
              import('@/features/curriculum/RulesScreen').then((m) => ({
                Component: m.RulesScreen,
              })),
          },
          {
            path: 'objectives/:objectiveId',
            lazy: () =>
              import('@/features/curriculum/LessonScreen').then((m) => ({
                Component: m.LessonScreen,
              })),
          },
          {
            path: 'results/:sessionId',
            lazy: () =>
              import('@/features/games/ResultsScreen').then((m) => ({
                Component: m.ResultsScreen,
              })),
          },
          { path: 'rewards', element: <RewardsScreen /> },
          { path: 'review', element: <ReviewScreen /> },
          { path: 'settings', element: <SettingsScreen /> },
        ],
      },
      {
        path: 'learn/play',
        element: <FocusLayout />,
        children: [
          {
            path: ':gameId',
            lazy: () =>
              import('@/features/games/GameScreen').then((m) => ({ Component: m.GameScreen })),
          },
        ],
      },
      {
        path: 'parent',
        lazy: () => import('./layouts/ParentLayout').then((m) => ({ Component: m.ParentLayout })),
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/parent/ParentDashboardScreen').then((m) => ({
                Component: m.ParentDashboardScreen,
              })),
          },
          {
            path: 'learners/:learnerId',
            lazy: () =>
              import('@/features/parent/LearnerReportScreen').then((m) => ({
                Component: m.LearnerReportScreen,
              })),
          },
          {
            path: 'data',
            lazy: () =>
              import('@/features/privacy/DataScreen').then((m) => ({ Component: m.DataScreen })),
          },
          {
            path: 'feedback',
            lazy: () =>
              import('@/features/feedback/FeedbackScreen').then((m) => ({
                Component: m.FeedbackScreen,
              })),
          },
          {
            path: 'vote',
            lazy: () =>
              import('@/features/feedback/VoteScreen').then((m) => ({
                Component: m.VoteScreen,
              })),
          },
          {
            path: 'security',
            lazy: () =>
              import('@/features/parent/SecurityScreen').then((m) => ({
                Component: m.SecurityScreen,
              })),
          },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
];
